"use strict";
import { getExploreDescription, getExploreTitle } from "pages/getExploreTitle";
import { getAddLiquidityPageTitle, getPositionPageDescription, getPositionPageTitle } from "pages/getPositionPageTitle";
import { Suspense, lazy, useMemo } from "react";
import { Navigate, matchPath, useLocation } from "react-router-dom";
import { isBrowserRouterEnabled } from "utils/env";
import Landing from "pages/Landing";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import i18n from "uniswap/src/i18n";
import SwapPage from "./Swap";
const CreatePosition = lazy(() => import("pages/Pool/Positions/create/CreatePosition"));
const AddLiquidityV3WithTokenRedirects = lazy(() => import("pages/AddLiquidityV3/redirects"));
const AddLiquidityV2WithTokenRedirects = lazy(() => import("pages/AddLiquidityV2/redirects"));
const RedirectExplore = lazy(() => import("pages/Explore/redirects"));
const NotFound = lazy(() => import("pages/NotFound"));
const Pool = lazy(() => import("pages/Pool"));
const DeliRewardsPanel = lazy(() => import("pages/Dev/DeliRewardsPanel"));
const LegacyPositionPageRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPositionPageRedirects }))
);
const RemoveLiquidityV2WithTokenRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.RemoveLiquidityV2WithTokenRedirects }))
);
const PositionPage = lazy(() => import("pages/Pool/Positions/PositionPage"));
const LegacyPositionPage = lazy(
  () => import("pages/Pool/Positions/LegacyPositionPage").then((module) => ({ default: module.LegacyPositionPage }))
);
const PoolDetails = lazy(() => import("pages/PoolDetails"));
const TokenDetails = lazy(() => import("pages/TokenDetails"));
export function useRouterConfig() {
  const browserRouterEnabled = isBrowserRouterEnabled();
  const { hash } = useLocation();
  const isEmbeddedWalletEnabled = useFeatureFlag(FeatureFlags.EmbeddedWallet);
  return useMemo(
    () => ({
      browserRouterEnabled,
      hash,
      isEmbeddedWalletEnabled
    }),
    [browserRouterEnabled, hash, isEmbeddedWalletEnabled]
  );
}
const StaticTitlesAndDescriptions = {
  UniswapTitle: i18n.t("title.uniswapTradeCrypto"),
  SwapTitle: i18n.t("title.buySellTradeEthereum"),
  SwapDescription: i18n.t("title.swappingMadeSimple"),
  DetailsPageBaseTitle: i18n.t("common.buyAndSell"),
  TDPDescription: i18n.t("title.realTime"),
  PDPDescription: i18n.t("title.tradeTokens"),
  MigrateTitle: i18n.t("title.migratev2"),
  MigrateTitleV3: i18n.t("title.migratev3"),
  MigrateDescription: i18n.t("title.easilyRemove"),
  MigrateDescriptionV4: i18n.t("title.easilyRemoveV4"),
  AddLiquidityDescription: i18n.t("title.earnFees"),
  PasskeyManagementTitle: i18n.t("title.managePasskeys")
};
function createRouteDefinition(route) {
  return {
    getElement: () => null,
    getTitle: () => StaticTitlesAndDescriptions.UniswapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    enabled: () => true,
    path: "/",
    nestedPaths: [],
    // overwrite the defaults
    ...route
  };
}
export const routes = [
  createRouteDefinition({
    path: "/",
    getTitle: () => StaticTitlesAndDescriptions.UniswapTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    getElement: (args) => {
      return args.browserRouterEnabled && args.hash ? <Navigate to={args.hash.replace("#", "")} replace /> : <Landing />;
    }
  }),
  createRouteDefinition({
    path: "/dev/deli-rewards",
    getTitle: () => "Deli Rewards Dev",
    getDescription: () => "Dev panel for Deli rewards endpoints",
    enabled: () => !!process.env.REACT_APP_ENABLE_DELI_REWARDS_DEV,
    getElement: () => <Suspense fallback={null}>
        <DeliRewardsPanel />
      </Suspense>
  }),
  createRouteDefinition({
    path: "/explore",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    nestedPaths: [":tab", ":chainName", ":tab/:chainName"],
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/explore/tokens/:chainName/:tokenAddress",
    getTitle: () => i18n.t("common.buyAndSell"),
    getDescription: () => StaticTitlesAndDescriptions.TDPDescription,
    getElement: () => <Suspense fallback={null}>
        <TokenDetails />
      </Suspense>
  }),
  createRouteDefinition({
    path: "/tokens",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    getElement: () => <Navigate to="/explore/tokens" replace />
  }),
  createRouteDefinition({
    path: "/tokens/:chainName",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/tokens/:chainName/:tokenAddress",
    getTitle: () => StaticTitlesAndDescriptions.DetailsPageBaseTitle,
    getDescription: () => StaticTitlesAndDescriptions.TDPDescription,
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/explore/pools/:chainName/:poolAddress",
    getTitle: () => StaticTitlesAndDescriptions.DetailsPageBaseTitle,
    getDescription: () => StaticTitlesAndDescriptions.PDPDescription,
    getElement: () => <Suspense fallback={null}>
        <PoolDetails />
      </Suspense>
  }),
  // createRouteDefinition({
  //   path: '/vote/*',
  //   getTitle: () => i18n.t('title.voteOnGov'),
  //   getDescription: () => i18n.t('title.uniToken'),
  //   getElement: () => {
  //     return (
  //       <Routes>
  //         <Route
  //           path="*"
  //           Component={() => {
  //             window.location.href = 'https://vote.uniswapfoundation.org'
  //             return null
  //           }}
  //         ></Route>
  //       </Routes>
  //     )
  //   },
  // }),
  // createRouteDefinition({
  //   path: '/create-proposal',
  //   getTitle: () => i18n.t('title.createGovernanceOn'),
  //   getDescription: () => i18n.t('title.createGovernanceTo'),
  //   getElement: () => <Navigate to="/vote/create-proposal" replace />,
  // }),
  // createRouteDefinition({
  //   path: '/buy',
  //   getElement: () => <Swap />,
  //   getTitle: () => StaticTitlesAndDescriptions.SwapTitle,
  // }),
  // createRouteDefinition({
  //   path: '/sell',
  //   getElement: () => <Swap />,
  //   getTitle: () => StaticTitlesAndDescriptions.SwapTitle,
  // }),
  // createRouteDefinition({
  //   path: '/send',
  //   getElement: () => <Swap />,
  //   getTitle: () => i18n.t('title.sendTokens'),
  // }),
  // createRouteDefinition({
  //   path: '/limits',
  //   getElement: () => <Navigate to="/limit" replace />,
  //   getTitle: () => i18n.t('title.placeLimit'),
  // }),
  // createRouteDefinition({
  //   path: '/limit',
  //   getElement: () => <Swap />,
  //   getTitle: () => i18n.t('title.placeLimit'),
  // }),
  // createRouteDefinition({
  //   path: '/buy',
  //   getElement: () => <Swap />,
  //   getTitle: () => StaticTitlesAndDescriptions.SwapTitle,
  // }),
  createRouteDefinition({
    path: "/swap",
    getElement: () => <SwapPage />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  // Refreshed pool routes
  createRouteDefinition({
    path: "/positions/create",
    getElement: () => <CreatePosition />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription,
    nestedPaths: [":protocolVersion"]
  }),
  createRouteDefinition({
    path: "/positions",
    getElement: () => <Pool />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v2/:chainName/:pairAddress",
    getElement: () => <LegacyPositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v3/:chainName/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v4/:chainName/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  // Legacy pool routes
  // createRouteDefinition({
  //   path: '/pool',
  //   getElement: () => <LegacyPoolRedirects />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  // createRouteDefinition({
  //   path: '/pool/v2',
  //   getElement: () => <LegacyPositionPageRedirects />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  // createRouteDefinition({
  //   path: '/pool/:tokenId',
  //   getElement: () => <LegacyPositionPageRedirects />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  // createRouteDefinition({
  //   path: '/pools/v2/find',
  //   getElement: () => <PoolFinderRedirects />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  // createRouteDefinition({
  //   path: '/pools',
  //   getElement: () => <LegacyPoolRedirects />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  // createRouteDefinition({
  //   path: '/pools/:tokenId',
  //   getElement: () => <LegacyPositionPageRedirects />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  createRouteDefinition({
    path: "/add/v2",
    nestedPaths: [":currencyIdA", ":currencyIdA/:currencyIdB"],
    getElement: () => <AddLiquidityV2WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/add",
    nestedPaths: [
      ":currencyIdA",
      ":currencyIdA/:currencyIdB",
      ":currencyIdA/:currencyIdB/:feeAmount",
      ":currencyIdA/:currencyIdB/:feeAmount/:tokenId"
    ],
    getElement: () => <AddLiquidityV3WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/remove/v2/:currencyIdA/:currencyIdB",
    getElement: () => <RemoveLiquidityV2WithTokenRedirects />,
    getTitle: () => i18n.t("title.removeLiquidityv2"),
    getDescription: () => i18n.t("title.removeTokensv2")
  }),
  createRouteDefinition({
    path: "/remove/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: () => i18n.t("title.removePoolLiquidity"),
    getDescription: () => i18n.t("title.removev3Liquidity")
  }),
  createRouteDefinition({ path: "*", getElement: () => <Navigate to="/not-found" replace /> }),
  createRouteDefinition({ path: "/not-found", getElement: () => <NotFound /> })
];
export const findRouteByPath = (pathname) => {
  for (const route of routes) {
    const match = matchPath(route.path, pathname);
    if (match) {
      return route;
    }
    const subPaths = route.nestedPaths.map((nestedPath) => `${route.path}/${nestedPath}`);
    for (const subPath of subPaths) {
      const match2 = matchPath(subPath, pathname);
      if (match2) {
        return route;
      }
    }
  }
  return void 0;
};
